import {
    api_admin_users_patch,
    api_admin_users_post,
    api_companies_users_patch,
    api_companies_users_post,
    Province,
    StaffRole,
    User,
} from 'abi'
import {
    addAlert,
    HoverInp,
    LoadingDots,
    PassCheck,
    setPopup,
} from 'components'
import {
    CheckFillIcon,
    CloseIcon,
    MapIcon,
    PassIcon,
    UserIcon,
    WarningIcon,
} from 'icons'
import { Component, createEffect, createMemo, on, Show } from 'solid-js'
import { createStore, produce, unwrap } from 'solid-js/store'

import './style/add.scss'

import Dropdown from 'components/dropdown'
import { PROVINCE_FA } from 'pages/panel/profile'
import { deepcopy, objentries } from 'utils/tools'
import { USTATE } from '.'
import { cState, isAdmin, isCorpManager, NoPermsForThisCorp } from '../shared'

interface AddUserCmpProps {
    add: USTATE['add']

    onClose(): void

    onAdd(c: User): void
    onEdit(c: User): void
}
export const AddUserCmp: Component<AddUserCmpProps> = P => {
    type STATE = {
        loading: boolean
        status: null | 'green' | 'red'

        name: string
        username: string
        pass: string

        role: StaffRole
        province: Province

        badPass: boolean

        anim: boolean
    }
    const STATE_DEFAULT: STATE = {
        loading: false,
        status: null,

        name: '',
        pass: '',
        username: '',

        province: 'none',

        role: 'agent',

        badPass: true,

        anim: false,
    }
    const [state, setState] = createStore<STATE>(deepcopy(STATE_DEFAULT))

    createEffect(
        on(
            () => P.add.show,
            () => {
                if (!P.add.show) closed()

                let u = unwrap(P.add.user)

                setState({
                    ...deepcopy(STATE_DEFAULT),
                    name: u?.name || '',
                    username: u?.username || '',
                    pass: '',
                    badPass: P.add.type === 'add' ? true : false,
                    role: P.add.user?.staff_role ?? STATE_DEFAULT['role'],
                    province: u?.province || 'none',
                })
            }
        )
    )

    const closed = () => {
        setState(deepcopy(STATE_DEFAULT))
    }

    const changed = createMemo(() => {
        let u = P.add.user
        let s = state

        if (P.add.type == 'add') {
            if (s.name.length !== 0 || s.username.length !== 0) return true
            return false
        }

        let c_role = u?.staff_role !== s.role
        let c_name = !!u?.name ? u?.name !== s.name : s.name.length !== 0
        let c_username = u?.username !== s.username
        let c_pass = s.badPass && s.pass.length > 0
        let c_province = s.province !== (u?.province || 'none')

        return c_name || c_username || c_pass || c_role || c_province
    })

    const isDisabled = createMemo(() => {
        return (
            (state.badPass && P.add.type == 'add') ||
            !state.name ||
            !state.username
        )
    })

    const checkForm = async () => {
        if (!isAdmin() && !isCorpManager(cState.corp!.id))
            return NoPermsForThisCorp()

        // begin checking
        const d = deepcopy(state)

        if (!cState.corp?.id)
            return addAlert({
                type: 'error',
                subject: 'خطا سرور',
                timeout: 3,
                content: 'لطفا صفحه را رفرش کنید.',
            })

        // 1) ADD: password is required and must be valid
        if (P.add.type === 'add' && (d.pass.length === 0 || d.badPass)) {
            return addAlert({
                type: 'error',
                subject: 'مشکل رمز',
                timeout: 3,
                content: 'رمز عبور را به درستی وارد کنید.',
            })
        }

        // 2) EDIT: password optional, but if user typed one it must be valid
        if (P.add.type === 'edit' && d.pass.length !== 0 && d.badPass) {
            return addAlert({
                type: 'error',
                subject: 'مشکل رمز',
                timeout: 3,
                content: 'رمز عبور جدید را به درستی وارد کنید.',
            })
        }

        if (!d.username) {
            return addAlert({
                type: 'error',
                subject: 'مشکل نام کاربری',
                timeout: 3,
                content: 'نام کاربری را به درستی وارد کنید.',
            })
        }

        if (!d.name) {
            return addAlert({
                type: 'error',
                subject: 'مشکل نام ',
                timeout: 3,
                content: 'نام نماینده را به درستی وارد کنید.',
            })
        }

        setState({ loading: true })

        let res

        let id = P.add.user?.id || -1

        if (P.add.type == 'add') {
            let createRes

            if (isAdmin()) {
                createRes = await api_admin_users_post(
                    { password: d.pass, username: d.username },
                    { show_alert: true }
                )
            }
            if (isCorpManager(cState.corp!.id)) {
                createRes = await api_companies_users_post(
                    {
                        cid: cState.corp!.id,
                    },
                    {
                        password: d.pass,
                        username: d.username,
                        name: d.name,
                        province: d.province,
                    },
                    { show_alert: true }
                )
            }
            setState({ loading: false })

            if (!createRes) return NoPermsForThisCorp()

            if (!createRes.ok()) {
                return setState({ status: 'red' })
            }
            id = createRes.body.id
        }

        let role: StaffRole = d.role

        if (!isAdmin()) {
            role = 'agent'
        }

        if (isAdmin()) {
            res = await api_admin_users_patch(
                { id },
                {
                    name: d.name,
                    banned: false,
                    password: d.pass || null,
                    username: d.username,
                    staff_at: cState.corp?.id,
                    staff_role: role,
                    province: d.province,
                },
                { show_alert: true }
            )
        }
        if (isCorpManager(cState.corp!.id)) {
            res = await api_companies_users_patch(
                { cid: cState.corp!.id, uid: id },
                {
                    name: d.name,
                    banned: false,
                    password: d.pass || null,
                    username: d.username,
                    province: d.province,
                },
                { show_alert: true }
            )
        }

        setState({ loading: false })

        if (!res) return NoPermsForThisCorp()

        if (!res.ok()) return setState({ status: 'red' })

        setState({ status: 'green' })

        if (P.add.type == 'edit') {
            P.onEdit({
                ...res.body,
                id: P.add.user!.id,
            })
        } else {
            pop_conffeties()
            P.onAdd({ ...res.body })
        }
    }

    const pop_conffeties = () => {
        // @ts-ignore
        if (typeof window.confetti !== 'function') return

        const count = 200,
            defaults = {
                origin: { y: 0.7 },
            }

        function fire(particleRatio: number, opts: any) {
            // @ts-ignore
            window.confetti(
                Object.assign({}, defaults, opts, {
                    particleCount: Math.floor(count * particleRatio),
                })
            )
        }

        fire(0.25, {
            spread: 26,
            startVelocity: 55,
        })

        fire(0.2, {
            spread: 60,
        })

        fire(0.35, {
            spread: 100,
            decay: 0.91,
            scalar: 0.8,
        })

        fire(0.1, {
            spread: 120,
            startVelocity: 25,
            decay: 0.92,
            scalar: 1.2,
        })

        fire(0.1, {
            spread: 120,
            startVelocity: 45,
        })
    }

    const OnClose = () => {
        if (state.loading) return

        if (state.status == 'red')
            return setState({ loading: false, status: null })

        if (changed() && !state.status) {
            return setPopup({
                show: true,
                content: 'بعد از خروج موارد تغییر یافته ذخیره نمیشوند!',
                Icon: WarningIcon,
                title: 'ذخیره نکردید!',
                type: 'warning',
                onSubmit() {
                    P.onClose()
                },
            })
        }

        P.onClose()
    }

    const SelectPerm: Component = () => {
        const isM = createMemo(() => state.role == 'manager')

        const setP = (role: StaffRole) => {
            setState({ role })
        }

        return (
            <div class='select-perm'>
                <div class='holder title_smaller'>
                    <span>نقش در شرکت</span>
                </div>
                <div class='picker title_smaller' classList={{ active: isM() }}>
                    <div
                        role='button'
                        class='pick'
                        onclick={() => setP('agent')}
                    >
                        نماینده
                    </div>
                    <div
                        role='button'
                        class='pick'
                        onclick={() => setP('manager')}
                    >
                        مدیرعامل
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div
            class='add-user-cmp'
            classList={{ show: P.add.show }}
            onclick={OnClose}
        >
            <div
                class='add-user-wrapper'
                onclick={e => {
                    e.stopPropagation()
                }}
            >
                <div
                    class='scaler'
                    onanimationend={() => setState({ anim: true })}
                ></div>

                <form
                    onsubmit={e => {
                        e.preventDefault()
                        checkForm()
                    }}
                    class='card'
                >
                    <button class='close icon' type='button' onclick={OnClose}>
                        <CloseIcon />
                    </button>
                    <div class='overflow-form'>
                        <div class='logo-wrapper'>
                            <img
                                class='logo'
                                decoding='async'
                                src='/public/imgs/logo.png'
                                alt=''
                            />
                            <div class='add-head'>
                                <p class='title_smaller sub'>اضافه کاربر در</p>
                                <p class='section_title main'>بیمه همراه</p>
                            </div>
                        </div>

                        <div class='inp-rows'>
                            <HoverInp
                                holder={'نام'}
                                inpType='text'
                                acitve={state.name.length !== 0}
                                value={state.name}
                                onInp={e =>
                                    setState(produce(s => (s.name = e)))
                                }
                                Icon={<UserIcon />}
                            />
                            <HoverInp
                                holder={'نام کاربری'}
                                inpType='username'
                                acitve={state.username.length !== 0}
                                value={state.username}
                                onInp={e =>
                                    setState(produce(s => (s.username = e)))
                                }
                                Icon={<UserIcon />}
                            />
                        </div>
                        <div class='inp-rows'>
                            <div class='province-pick'>
                                <div class='label title_smaller'>
                                    <MapIcon />
                                    استان کاربر
                                </div>
                                <Dropdown
                                    active={PROVINCE_FA[state.province]}
                                    items={objentries(PROVINCE_FA)
                                        .filter(([k]) => k !== state.province)
                                        .map(([k, v]) => ({
                                            name: v,
                                            value: k,
                                        }))}
                                    onchange={k =>
                                        setState('province', k as Province)
                                    }
                                    maxHeight={200}
                                />
                            </div>
                            <HoverInp
                                holder={'کلمه عبور'}
                                inpType='password'
                                acitve={state.pass.length !== 0}
                                value={state.pass}
                                onInp={e =>
                                    setState(produce(s => (s.pass = e)))
                                }
                                Icon={<PassIcon />}
                            />

                            <PassCheck
                                maxLength={12}
                                pass={state.pass}
                                show={state.pass.length !== 0}
                                onPassed={s => setState({ badPass: !s })}
                            />

                            <Show when={isAdmin()}>
                                <SelectPerm />
                            </Show>
                        </div>
                    </div>

                    <div class='cta-wrapper title_small'>
                        <button
                            class='cta'
                            type='submit'
                            classList={{ disable: isDisabled() || !changed() }}
                        >
                            ثبت
                        </button>
                    </div>

                    <div
                        class='loader title_hero'
                        classList={{ show: state.loading }}
                    >
                        <LoadingDots />
                        درحال ثبت کاربر
                    </div>
                    <div
                        class='status-wrapper'
                        classList={{
                            show: !!state.status,
                            [state.status || '']: true,
                        }}
                    >
                        <Show
                            when={state.status == 'green'}
                            fallback={<CloseIcon />}
                        >
                            <CheckFillIcon />
                        </Show>

                        <p class='title_hero'>
                            <Show
                                when={state.status == 'green'}
                                fallback={
                                    'خطا در ثبت کاربر، لطفا دوباره تلاش کنید.'
                                }
                            >
                                کاربر {state.name} با موفقیت ثبت شد!
                            </Show>
                        </p>
                    </div>
                </form>
            </div>
        </div>
    )
}
